import React from 'react';
import { Instagram, Mail, Phone, MapPin, Clock } from 'lucide-react';
import { contactInfo } from '../data/mockData';
import { Card } from './ui/card';

const Contact = () => {
  const contactMethods = [
    {
      icon: <Phone size={24} />,
      title: "WhatsApp",
      info: contactInfo.whatsapp,
      link: `https://wa.me/598${contactInfo.whatsapp.replace(/\s/g, '')}`
    },
    {
      icon: <Instagram size={24} />,
      title: "Instagram",
      info: contactInfo.instagram,
      link: "https://instagram.com/d_lyrioestampados"
    },
    {
      icon: <Mail size={24} />,
      title: "Email",
      info: contactInfo.email,
      link: `mailto:${contactInfo.email}`
    },
    {
      icon: <MapPin size={24} />,
      title: "Ubicación",
      info: contactInfo.direccion,
      link: null
    }
  ];

  return (
    <section id="contacto" className="py-24 bg-cream-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-primary-pink text-sm tracking-widest uppercase mb-4 font-medium">
            Conecta con Nosotros
          </p>
          <h2 className="text-4xl md:text-5xl font-serif text-charcoal mb-6">
            Contacto
          </h2>
          <div className="w-24 h-1 bg-primary-pink mx-auto mb-6"></div>
          <p className="text-warm-gray text-lg max-w-2xl mx-auto">
            ¿Tienes alguna pregunta? Estamos aquí para ayudarte. Contáctanos por tu medio preferido.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Methods */}
          <div className="space-y-6">
            {contactMethods.map((method, index) => (
              <Card
                key={index}
                className="bg-white border-none shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
              >
                {method.link ? (
                  <a
                    href={method.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 group"
                  >
                    <div className="w-14 h-14 bg-primary-pink/10 rounded-full flex items-center justify-center text-primary-pink group-hover:bg-primary-pink group-hover:text-cream-white transition-all duration-300">
                      {method.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-serif text-charcoal mb-1 group-hover:text-primary-pink transition-colors">
                        {method.title}
                      </h3>
                      <p className="text-warm-gray">{method.info}</p>
                    </div>
                  </a>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-primary-pink/10 rounded-full flex items-center justify-center text-primary-pink">
                      {method.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-serif text-charcoal mb-1">
                        {method.title}
                      </h3>
                      <p className="text-warm-gray">{method.info}</p>
                    </div>
                  </div>
                )}
              </Card>
            ))}

            {/* Horario */}
            <Card className="bg-dusty-rose/30 border-none p-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-primary-pink/10 rounded-full flex items-center justify-center text-primary-pink flex-shrink-0">
                  <Clock size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-serif text-charcoal mb-2">
                    Horario de Atención
                  </h3>
                  <p className="text-warm-gray">{contactInfo.horario}</p>
                  <p className="text-sm text-warm-gray mt-2">
                    📦 Hacemos envíos en San José de Mayo y resto del país
                  </p>
                  <p className="text-sm text-warm-gray">
                    ✅ Garantía de 1 mes en todas las compras
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Image & Info Card */}
          <div className="space-y-6">
            <Card className="bg-white border-none shadow-xl overflow-hidden">
              <img
                src="/images/contact/dtf_vanguardia.png"
                alt="Printing Technology"
                className="w-full h-64 object-cover"
              />
              <div className="p-8">
                <h3 className="text-2xl font-serif text-charcoal mb-4">
                  Tecnología DTF de Vanguardia
                </h3>
                <p className="text-warm-gray leading-relaxed mb-6">
                  Utilizamos equipos de última generación para garantizar la mejor calidad 
                  en cada estampado. Nuestro proceso DTF asegura colores vibrantes, 
                  durabilidad excepcional y un acabado profesional.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary-pink/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 bg-primary-pink rounded-full"></div>
                    </div>
                    <p className="text-sm text-warm-gray">
                      <strong>Calidad Premium:</strong> Impresión de alta resolución
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary-pink/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 bg-primary-pink rounded-full"></div>
                    </div>
                    <p className="text-sm text-warm-gray">
                      <strong>Versatilidad:</strong> Compatible con todo tipo de telas
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary-pink/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="w-2 h-2 bg-primary-pink rounded-full"></div>
                    </div>
                    <p className="text-sm text-warm-gray">
                      <strong>Resistencia:</strong> Diseños que duran lavado tras lavado
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* CTA Card */}
            <Card className="bg-gradient-to-br from-primary-pink to-dusty-rose border-none p-8 text-center shadow-xl">
              <h3 className="text-2xl font-serif text-cream-white mb-4">
                ¿Listo para crear tu diseño?
              </h3>
              <p className="text-cream-white/90 mb-6">
                Contáctanos ahora y transforma tus ideas en realidad
              </p>
              <a
                href="https://wa.me/59898836023"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-cream-white text-primary-pink hover:bg-deep-navy hover:text-cream-white px-8 py-3 rounded font-medium tracking-wide transition-all duration-300 shadow-lg"
              >
                <Phone size={20} className="mr-2" />
                CONTACTAR POR WHATSAPP
              </a>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
